"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SalesOrderCartItems = void 0;
const React = require("react");
const react_1 = require("react");
const useRequiredContext_1 = require("../../../../../hooks/useRequiredContext");
const SalesOrderContext_1 = require("../../contexts/SalesOrderContext");
const List_1 = require("../../../../../components/base/structure/List");
const TSalesOrderItemType_1 = require("../../types/TSalesOrderItemType");
const ProductBuilderModal_1 = require("../../modals/ProductBuilderModal");
const ProductDetails_1 = require("./ProductDetails");
const PromotionLine_1 = require("./PromotionLine");
const ProductEditSection_1 = require("./ProductEditSection");
const CartTotal_1 = require("./CartTotal");
const ClearCartButton_1 = require("./ClearCartButton");
const styles = require("./styles.scss");
const cssComposer_1 = require("../../../../../helpers/cssComposer");
const css = (0, cssComposer_1.default)(styles, "SalesOrderCartItems");
/**
 * Cart summary showing:
 *
 * - Item ordered and individual cost
 * - Quantity ordered
 * - Total amount for item
 *
 * Also actions:
 *
 *  - Remove item
 *  - Change quantity
 *  - Edit built product
 */
const SalesOrderCartItems = ({ salesOrderItems, totalPrice, promotions, editable = false, showTotal = true, className }) => {
    const { updateSalesOrderItemBuilt, updateSalesOrderItemQuantity, removeSalesOrderItemFromSalesOrder, isUpdatingCart, temporarySalesOrderId } = (0, useRequiredContext_1.useRequiredContext)(SalesOrderContext_1.SalesOrderContext);
    // we build a simple map object so we know which item in the cart we are editing
    const [salesOrderItemBuiltDetails, setSalesOrderItemBuiltDetails] = (0, react_1.useState)(null);
    const builderPromise = (0, react_1.useRef)();
    // whether cart is populated
    const hasItems = (0, react_1.useMemo)(() => Array.isArray(salesOrderItems) && salesOrderItems.length > 0, [salesOrderItems]);
    // update a product in the cart with a new quantity
    const updateProductQuantity = (0, react_1.useCallback)(async (salesOrderItem, newQuantity, index) => {
        await updateSalesOrderItemQuantity(salesOrderItem, newQuantity, index);
        return new Promise((resolve, reject) => {
            builderPromise.current = { resolve, reject };
        });
    }, [updateSalesOrderItemQuantity, temporarySalesOrderId]);
    // update a build product in the cart to the newly updated one
    const updateProduct = (0, react_1.useCallback)(async (salesOrderItemBuilt, index) => {
        await updateSalesOrderItemBuilt(salesOrderItemBuilt, index);
        return new Promise((resolve, reject) => {
            builderPromise.current = { resolve, reject };
        });
    }, [updateSalesOrderItemBuilt, temporarySalesOrderId]);
    // callback to run when finished editing a built product
    const completeEditingBuild = (0, react_1.useCallback)((salesOrderItem) => {
        // modal was closed
        if (!salesOrderItem) {
            setSalesOrderItemBuiltDetails(null);
            return;
        }
        // editing existing so pull from state
        const { index } = salesOrderItemBuiltDetails;
        if (salesOrderItem) {
            updateProduct(salesOrderItem, index)
                .then(() => builderPromise.current.resolve(true))
                .catch(() => builderPromise.current.reject(false));
        }
        else {
            builderPromise.current.resolve(false);
        }
        // reset current editing product
        setSalesOrderItemBuiltDetails(null);
    }, [salesOrderItemBuiltDetails, temporarySalesOrderId]);
    if (!hasItems) {
        return (React.createElement("div", { className: css("", { [`-noItems`]: true }) },
            React.createElement(List_1.ListItemWrapper, { className: className },
                React.createElement(List_1.ListItemBody, null,
                    React.createElement(List_1.List, { variant: "separator", scale: "normal" },
                        React.createElement(List_1.ListItem, null,
                            React.createElement("span", { className: css("noItemsPlaceholder") }, "No items in cart")))))));
    }
    const lineItems = salesOrderItems.map((salesOrderItem, index) => (React.createElement(List_1.ListItemWrapper, { key: `cart-item-${index}`, className: className },
        React.createElement(List_1.ListItemBody, null,
            React.createElement("div", { className: css("lineItem") },
                React.createElement(ProductDetails_1.ProductDetails, { salesOrderItem: salesOrderItem, removeSalesOrderItemFromSalesOrder: removeSalesOrderItemFromSalesOrder, isUpdatingCart: isUpdatingCart, editable: editable, updateProductQuantity: updateProductQuantity, index: index, builderPromise: builderPromise, setSalesOrderItemBuiltDetails: setSalesOrderItemBuiltDetails })),
            salesOrderItem.type === TSalesOrderItemType_1.TSalesOrderItemType.Built && editable && (React.createElement(ProductEditSection_1.ProductEditSection, { salesOrderItem: salesOrderItem, removeSalesOrderItemFromSalesOrder: removeSalesOrderItemFromSalesOrder, isUpdatingCart: isUpdatingCart, updateProductQuantity: updateProductQuantity, index: index, builderPromise: builderPromise, setSalesOrderItemBuiltDetails: setSalesOrderItemBuiltDetails }))))));
    if (Array.isArray(promotions)) {
        promotions.map((promotion, index) => {
            lineItems.push(React.createElement(PromotionLine_1.PromotionLine, { key: `promotion-line-${index}`, promotion: promotion }));
        });
    }
    if (showTotal) {
        lineItems.push(React.createElement(CartTotal_1.CartTotal, { key: `cart-total`, totalPrice: totalPrice }));
    }
    return (React.createElement("div", { className: css("", { className }) },
        React.createElement(List_1.List, { variant: "separator", scale: "normal" }, lineItems),
        React.createElement(ClearCartButton_1.ClearCartButton, { editable: editable }),
        React.createElement(ProductBuilderModal_1.ProductBuilderModal, { salesOrderItemBuilt: salesOrderItemBuiltDetails === null || salesOrderItemBuiltDetails === void 0 ? void 0 : salesOrderItemBuiltDetails.salesOrderItemBuilt, isExisting // whether to add new or update existing built product
            : true, onCompletion: completeEditingBuild })));
};
exports.SalesOrderCartItems = SalesOrderCartItems;
//# sourceMappingURL=index.js.map